import type {AdminState, AdminUser} from './types'
import {type AdminAction, AdminActionType} from '../actions/AdminActions'

export const initialAdminState: AdminState = {
  products: [],
  productsPending: false,
  productsError: '',
  users: [],
  usersPending: false,
  usersError: '',
  notAuthorizedUsers: [],
  notAuthorizedUsersPending: false,
  notAuthorizedUsersError: ''
}


// eslint-disable-next-line complexity -- reducers need to handle all action types
export function adminReducer(state = initialAdminState, action: AdminAction): AdminState {
  // Get users
  if (action.type === AdminActionType.ADMIN_GET_USERS_PENDING) {
    return {
      ...state,
      usersPending: true
    }
  }
  if (action.type === AdminActionType.ADMIN_GET_USERS_FULFILLED) {
    return {
      ...state,
      usersPending: false,
      users: action.payload,
      usersError: ''
    }
  }
  if (action.type === AdminActionType.ADMIN_GET_USERS_REJECTED) {
    return {
      ...state,
      usersPending: false,
      users: [],
      usersError: action.payload.error
    }
  }
  // Get not-authorized users
  if (action.type === AdminActionType.ADMIN_GET_NOT_AUTHORIZED_USERS_PENDING) {
    return {
      ...state,
      notAuthorizedUsersPending: true
    }
  }
  if (action.type === AdminActionType.ADMIN_GET_NOT_AUTHORIZED_USERS_FULFILLED) {
    return {
      ...state,
      notAuthorizedUsersPending: false,
      notAuthorizedUsers: action.payload,
      notAuthorizedUsersError: ''
    }
  }
  if (action.type === AdminActionType.ADMIN_GET_NOT_AUTHORIZED_USERS_REJECTED) {
    return {
      ...state,
      notAuthorizedUsersPending: false,
      notAuthorizedUsers: [],
      notAuthorizedUsersError: action.payload.error
    }
  }
  // Update user access
  if (action.type === AdminActionType.ADMIN_UPDATE_USER_ACCESS_PENDING) {
    return {
      ...state,
      usersPending: true
    }
  }
  if (action.type === AdminActionType.ADMIN_UPDATE_USER_ACCESS_FULFILLED) {
    const {payload} = action
    const users = toUpdatedUsers(state, payload);
    const notAuthorizedUsers = toUpdatedNotAuthorizedUsers(state, payload);

    return {
      ...state,
      usersPending: false,
      users,
      usersError: '',
      notAuthorizedUsers
    }
  }
  if (action.type === AdminActionType.ADMIN_UPDATE_USER_ACCESS_REJECTED) {
    return {
      ...state,
      usersPending: false,
      usersError: action.payload.error
    }
  }

  // Get products
  if (action.type === AdminActionType.ADMIN_GET_PRODUCTS_PENDING) {
    return {
      ...state,
      productsPending: true
    }
  }
  if (action.type === AdminActionType.ADMIN_GET_PRODUCTS_FULFILLED) {
    return {
      ...state,
      productsPending: false,
      products: action.payload,
      productsError: ''
    }
  }
  if (action.type === AdminActionType.ADMIN_GET_PRODUCTS_REJECTED) {
    return {
      ...state,
      productsPending: false,
      products: [],
      productsError: action.payload.error
    }
  }

  // Update product
  if (action.type === AdminActionType.ADMIN_UPDATE_PRODUCT_PENDING) {
    return {
      ...state,
      productsPending: true
    }
  }
  if (action.type === AdminActionType.ADMIN_UPDATE_PRODUCT_FULFILLED) {
    return {
      ...state,
      productsPending: false,
      products: state.products.map(p => p.id === action.payload.id ? {...action.payload} : p),
      productsError: ''
    }
  }
  if (action.type === AdminActionType.ADMIN_UPDATE_PRODUCT_REJECTED) {
    return {
      ...state,
      productsPending: false,
      productsError: action.payload.error
    }
  }

  return state
}

/* Helper Functions */

function toUpdatedUsers(state: AdminState, payload: AdminUser): AdminUser[] {
  const isNew = state.users.find(u => u.email === payload.email) == null

  if (isNew) {
    return state.users.concat([payload])
  }

  const isRemove = !payload.useAuthorized
  if (isRemove) {
    return state.users.filter(u => u.email !== payload.email)
  }

  return state.users.map(u => u.email === payload.email ? payload : u)
}

function toUpdatedNotAuthorizedUsers(state: AdminState, payload: AdminUser): AdminUser[] {
  const found = state.notAuthorizedUsers.find(u => u.email === payload.email) != null

  // Existing entry update
  if (found) {
    if (payload.useAuthorized) {
      return state.notAuthorizedUsers.filter(u => u.email !== payload.email)
    }
    return state.notAuthorizedUsers.map(u => u.email === payload.email ? payload : u)

  }

  // New entry addition
  const isNotAuthorized = !payload.useAuthorized
  if (isNotAuthorized) {
    return state.notAuthorizedUsers.concat([payload])
  }

  return state.notAuthorizedUsers
}
